import { Component, OnDestroy, OnInit } from '@angular/core';
import { Store } from '@ngrx/store';
import {
  BehaviorSubject,
  combineLatest,
  interval,
  of,
  Subject,
  Subscription,
} from 'rxjs';
import { filter, flatMap, startWith, takeUntil, tap } from 'rxjs/operators';
import { AppState } from 'src/app';
import { config } from 'src/app/environment-config';
import { selectIsUsa } from 'src/app/state/selectors/current-contract.selectors';
import { CoachmarkService } from 'src/components/common/coachmark/coachmark.service';
import { MarketService } from 'src/core/services/market.service';
import { coachmarks } from '@models/coachmark.class';
import { TradingMarketConfiguration } from '@models/trading-market-configuration.class';

const { timeOuts_timeOut1Min } = config();
@Component({
  selector: 'hb-market-footer-bar',
  templateUrl: './market-footer-bar.component.html',
  styleUrls: ['./market-footer-bar.component.scss'],
})
export class MarketFooterBarComponent implements OnInit, OnDestroy {
  public showTooltipMarket = false;
  public isOutOfOperationTime = false;
  public isOutOfOperationTimeMoreOneDay = false;
  public isOutOfOperationBeforeMarketTime = false;
  public tooltipMarketText: string;
  public timeLeftMarket: {
    hours: number;
    minutes: number;
  };
  public marketConfiguration: Partial<TradingMarketConfiguration>;
  public anchorId = coachmarks.footbar.market;
  private isUsaContract: boolean;
  private subscriptions = new Subscription();
  private destroyed$: Subject<void> = new Subject();
  private timeLeft$ = new BehaviorSubject(1);

  private marketTooltipText: {
    [key: string]: {
      [key: string]: string;
    };
  } = {
    open: {
      usa: 'Las órdenes enviadas después del cierre se procesarán el siguiente día hábil mexicano y estadounidense',
      mx: 'Las órdenes enviadas después del cierre se procesarán el siguiente día hábil mexicano',
    },
    close: {
      usa: 'Las órdenes enviadas se procesarán cuando vuelva abrir el mercado',
      mx: 'Las órdenes enviadas se procesarán cuando vuelva abrir el mercado',
    },
  };

  private tooltipOptionsDate: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  };
  constructor(
    private readonly coachmarkService: CoachmarkService,
    private readonly marketService: MarketService,
    private readonly store: Store<AppState>,
  ) {}

  ngOnInit(): void {
    this.subscriptions.add(
      combineLatest(
        this.store.select(selectIsUsa).pipe(
          filter((isUsa) => isUsa !== undefined),
          tap((isUsa) => (this.isUsaContract = isUsa)),
          flatMap((isUsa) =>
            this.marketService.getTradingMarketConfiguration(isUsa),
          ),
        ),
        this.timeLeft$,
      ).subscribe(([marketConfiguration]) => {
        this.marketConfiguration = marketConfiguration;

        const now = new Date();

        this.isOutOfOperationTime =
          !this.marketConfiguration.isWorkingDay ||
          new Date(this.marketConfiguration.startTime).getTime() >
            now.getTime() ||
          now.getTime() > new Date(this.marketConfiguration.endTime).getTime();

        this.isOutOfOperationBeforeMarketTime =
          new Date(this.marketConfiguration.startTime).getTime() >
          now.getTime();

        this.tooltipMarketText =
          this.marketTooltipText[
            this.isOutOfOperationTime
              ? openMarketEnum.close
              : openMarketEnum.open
          ][this.isUsaContract ? marketTypeEnum.usa : marketTypeEnum.mx];

        if (!this.isOutOfOperationTime) {
          /**
           * Market opend
           */
          this.setObservableOpenMarketTimeLeft();
        } else if (!this.isOutOfOperationBeforeMarketTime) {
          /**
           * Market closed
           */
          this.isOutOfOperationTimeMoreOneDay =
            this.getDateDiff(
              new Date(this.marketConfiguration.nextWorkingDate),
              now,
              DateType.days,
            ) > 0;

          if (this.isOutOfOperationTimeMoreOneDay) {
            /**
             * Market closed for more > 1 day
             */
            const nextWorkingDate = new Date(
              this.marketConfiguration.nextWorkingDate,
            );

            const hours = nextWorkingDate
              .toTimeString()
              .split(' ')[0]
              .split(':')[0];
            const minutes = nextWorkingDate
              .toTimeString()
              .split(' ')[0]
              .split(':')[1];

            this.tooltipMarketText = `Las órdenes se procesarán el ${nextWorkingDate.toLocaleDateString(
              'es-MX',
              this.tooltipOptionsDate,
            )}, a partir de las ${hours}:${minutes}  hrs `;
          }
        }
        this.initMarketFooterCoachmark();
      }),
    );
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.subscriptions.unsubscribe();
  }

  private initMarketFooterCoachmark(): void {
    this.coachmarkService.initializeTour(this.anchorId);
  }

  private setObservableOpenMarketTimeLeft(): void {
    interval(Number(timeOuts_timeOut1Min))
      .pipe(startWith(0), takeUntil(this.destroyed$))
      .subscribe(() => this.setOpenMarketTimeLeft());
  }

  private setOpenMarketTimeLeft(): void {
    const now = new Date();

    const hoursLeft = this.getDateDiff(
      new Date(this.marketConfiguration.endTime),
      now,
      DateType.hours,
    );

    const minutesLeft =
      this.getDateDiff(
        new Date(this.marketConfiguration.endTime),
        now,
        DateType.minutes,
      ) % 60;

    if (hoursLeft > -1) {
      this.timeLeftMarket = {
        hours: hoursLeft,
        minutes: minutesLeft,
      };
    } else {
      this.timeLeft$.next(hoursLeft);
      this.destroyed$.next();
    }
  }

  private getDateDiff(
    firstDate: Date,
    secondDate: Date,
    type: DateType,
  ): number {
    const diff = firstDate.getTime() - secondDate.getTime();
    switch (type) {
      case DateType.minutes:
        return Math.round(diff / 1000 / 60);
      case DateType.hours:
        return Math.floor(diff / 1000 / 60 / 60);
      case DateType.days:
        return Math.floor(diff / 1000 / 60 / 60 / 24);
      default:
        return diff;
    }
  }
}

enum DateType {
  minutes = 'minutes',
  hours = 'hours',
  days = 'days',
}

enum openMarketEnum {
  open = 'open',
  close = 'close',
}

enum marketTypeEnum {
  usa = 'usa',
  mx = 'mx',
}
